import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import usePersistentFilters from "../Common/usePersistentFilters";
import { Badge, Button, Card, Col, Form, Modal, Row, Spinner, Table } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import swal from "sweetalert";
import api, { getFileUrl } from "../../../api";
import PageTitle from "../../layouts/PageTitle";

// Constants 
const statusVariant = {
    PRESENT: "success",
    MISSING_PUNCH: "warning",
    HALF_DAY: "warning",
    ABSENT: "danger",
    LEAVE: "info",
    WEEKLY_OFF: "secondary",
    HOLIDAY: "primary",
    MANUAL: "dark",
};

const statusOptions = [
    { value: "", label: "All Status" },
    { value: "PRESENT", label: "Present" },
    { value: "HALF_DAY", label: "Half Day" },
    { value: "ABSENT", label: "Absent" },
    { value: "LEAVE", label: "Paid Leave" },
    { value: "WEEKLY_OFF", label: "Weekly Off" },
    { value: "HOLIDAY", label: "Holiday" },
    { value: "MANUAL", label: "Manual" },
];

const toDateInputValue = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
};

const getDefaultFilters = () => {
    const now = new Date();
    return {
        fromDate: toDateInputValue(new Date(now.getFullYear(), now.getMonth(), 1)),
        toDate: toDateInputValue(now),
        status: "",
    };
};

// Helpers 
const formatDateTime = (value) => {
    if (!value) return "-";
    return new Date(value).toLocaleString("en-IN", {
        day: "2-digit", month: "short", year: "numeric",
        hour: "2-digit", minute: "2-digit",
    });
};

const formatDuration = (minutes) => {
    const total = Number(minutes || 0);
    if (!total) return "-";
    const hours = Math.floor(total / 60);
    const mins = total % 60;
    return `${hours}h ${mins}m`;
};

const getCurrentLocation = () =>
    new Promise((resolve, reject) => {
        if (!navigator.geolocation) {
            reject(new Error("Location is not supported in this browser"));
            return;
        }
        navigator.geolocation.getCurrentPosition(
            (pos) => resolve({
                latitude: pos.coords.latitude,
                longitude: pos.coords.longitude,
                accuracy: pos.coords.accuracy,
            }),
            () => reject(new Error("Allow location permission to punch attendance")),
            { enableHighAccuracy: true, timeout: 10000, maximumAge: 30000 },
        );
    });

const getMapUrl = (loc) => {
    if (!loc?.latitude || !loc?.longitude) return "";
    return `https://www.google.com/maps?q=${loc.latitude},${loc.longitude}`;
};

// Capture a frame from a video element as a Blob (JPEG)
const captureFrameAsBlob = (videoEl) =>
    new Promise((resolve, reject) => {
        try {
            const canvas = document.createElement("canvas");
            canvas.width = videoEl.videoWidth || 640;
            canvas.height = videoEl.videoHeight || 480;
            canvas.getContext("2d").drawImage(videoEl, 0, 0, canvas.width, canvas.height);
            canvas.toBlob((blob) => {
                if (blob) resolve(blob);
                else reject(new Error("Failed to capture photo"));
            }, "image/jpeg", 0.85);
        } catch (err) {
            reject(err);
        }
    });

// Camera Modal Component 
const CameraModal = ({ show, punchType, onCapture, onCancel }) => {
    const videoRef = useRef(null);
    const streamRef = useRef(null);
    const [cameraReady, setCameraReady] = useState(false);
    const [capturing, setCapturing] = useState(false);
    const [previewUrl, setPreviewUrl] = useState(null);
    const [capturedBlob, setCapturedBlob] = useState(null);
    const [cameraError, setCameraError] = useState("");

    const startCamera = useCallback(async () => {
        setCameraError("");
        setCameraReady(false);
        setPreviewUrl(null);
        setCapturedBlob(null);
        try {
            const stream = await navigator.mediaDevices.getUserMedia({
                video: { facingMode: "user", width: { ideal: 640 }, height: { ideal: 480 } },
                audio: false,
            });
            streamRef.current = stream;
            if (videoRef.current) {
                videoRef.current.srcObject = stream;
                videoRef.current.onloadedmetadata = () => {
                    videoRef.current.play();
                    setCameraReady(true);
                };
            }
        } catch (err) {
            setCameraError("Camera access denied. Please allow camera permission and try again.");
        }
    }, []);

    const stopCamera = useCallback(() => {
        if (streamRef.current) {
            streamRef.current.getTracks().forEach((t) => t.stop());
            streamRef.current = null;
        }
        setCameraReady(false);
    }, []);

    useEffect(() => {
        if (show) startCamera();
        else stopCamera();
        return () => stopCamera();
    }, [show, startCamera, stopCamera]);

    const handleCapture = async () => {
        if (!videoRef.current || !cameraReady) return;
        try {
            setCapturing(true);
            const blob = await captureFrameAsBlob(videoRef.current);
            const url = URL.createObjectURL(blob);
            setCapturedBlob(blob);
            setPreviewUrl(url);
            stopCamera();
        } catch (err) {
            setCameraError("Failed to capture photo. Please try again.");
        } finally {
            setCapturing(false);
        }
    };

    const handleRetake = () => {
        if (previewUrl) URL.revokeObjectURL(previewUrl);
        setPreviewUrl(null);
        setCapturedBlob(null);
        startCamera();
    };

    const handleConfirm = () => {
        if (!capturedBlob) return;
        const url = previewUrl;
        onCapture(capturedBlob, url);
    };

    return (
        <Modal show={show} onHide={onCancel} centered size="md" backdrop="static">
            <Modal.Header closeButton>
                <Modal.Title>
                    {punchType === "in" ? " Punch In — Capture Photo" : " Punch Out — Capture Photo"}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body className="text-center">
                {cameraError && <div className="alert alert-danger py-2 small">{cameraError}</div>}

                {!previewUrl ? (
                    <div className="camera-viewfinder-wrap">
                        <video
                            ref={videoRef}
                            autoPlay
                            playsInline
                            muted
                            className="camera-viewfinder"
                            style={{ width: "100%", maxHeight: 360, borderRadius: 10, background: "#000", display: cameraReady ? "block" : "none" }}
                        />
                        {!cameraReady && !cameraError && (
                            <div className="d-flex justify-content-center align-items-center" style={{ height: 200 }}>
                                <Spinner animation="border" variant="primary" />
                                <span className="ms-2 text-muted">Starting camera...</span>
                            </div>
                        )}
                    </div>
                ) : (
                    <div>
                        <img src={previewUrl} alt="Captured" style={{ width: "100%", maxHeight: 360, borderRadius: 10, objectFit: "cover" }} />
                        <div className="text-success small mt-2"> Photo captured</div>
                    </div>
                )}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={onCancel}>Cancel</Button>
                {!previewUrl ? (
                    <Button variant="primary" onClick={handleCapture} disabled={!cameraReady || capturing}>
                        {capturing ? "Capturing..." : " Capture"}
                    </Button>
                ) : (
                    <>
                        <Button variant="outline-secondary" onClick={handleRetake}>Retake</Button>
                        <Button variant={punchType === "in" ? "success" : "danger"} onClick={handleConfirm}>
                            {punchType === "in" ? "Confirm Punch In" : "Confirm Punch Out"}
                        </Button>
                    </>
                )}
            </Modal.Footer>
        </Modal>
    );
};

// Main Component 
const StaffSelfAttendance = () => {
    const navigate = useNavigate();
    const [today, setToday] = useState(null);
    const [employee, setEmployee] = useState(null);
    const [history, setHistory] = useState([]);
    const [filters, setFilters] = usePersistentFilters("staff_self_attendance_filters", getDefaultFilters);
    const [appliedFilters, setAppliedFilters] = useState(getDefaultFilters);
    const [loading, setLoading] = useState(false);
    const [actionLoading, setActionLoading] = useState(null);
    const [accessMessage, setAccessMessage] = useState("");
    const [canPunchToday, setCanPunchToday] = useState(false);
    const [punchDisabledMessage, setPunchDisabledMessage] = useState("");

    // Camera modal state
    const [cameraOpen, setCameraOpen] = useState(false);
    const [pendingPunchType, setPendingPunchType] = useState(null); // "in" | "out"
    const pendingLocationRef = useRef(null);

    const activeFilterCount = useMemo(
        () => [appliedFilters.fromDate, appliedFilters.toDate, appliedFilters.status].filter(Boolean).length,
        [appliedFilters],
    );

    const todayState = useMemo(() => {
        if (!today) return "NOT_PUNCHED";
        if (today.punchInAt && !today.punchOutAt) return "IN";
        if (today.punchInAt && today.punchOutAt) return "OUT";
        return "NOT_PUNCHED";
    }, [today]);

    const loadAttendance = useCallback(async (currentFilters) => {
        try {
            setLoading(true);
            setAccessMessage("");
            const todayRes = await api.get("/staff-attendance/self/today");

            if (!todayRes.data.employee) {
                setAccessMessage(todayRes.data.message || "Self attendance is available only for payroll staff");
                setToday(null); setEmployee(null); setHistory([]);
                setCanPunchToday(false); setPunchDisabledMessage("");
                return;
            }

            const historyRes = await api.get("/staff-attendance/self/history", {
                params: currentFilters,
            });

            setToday(todayRes.data.record || null);
            setEmployee(todayRes.data.employee || historyRes.data.employee || null);
            setHistory(historyRes.data.data || []);
            setCanPunchToday(Boolean(todayRes.data.canPunch));
            setPunchDisabledMessage(todayRes.data.canPunch ? "" : todayRes.data.message || "Punch is disabled for today");
        } catch (error) {
            setAccessMessage(error.response?.data?.message || "Self attendance is available only for payroll staff");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadAttendance(appliedFilters);
    }, [appliedFilters, loadAttendance]);

    const updateFilter = (field, value) => setFilters((c) => ({ ...c, [field]: value }));
    const applyFilters = () => setAppliedFilters({ ...filters });
    const clearFilters = () => {
        const next = getDefaultFilters();
        setFilters(next);
        setAppliedFilters(next);
    };

    // Step 1: get location first, then open camera
    const startPunch = async (type) => {
        try {
            setActionLoading(type);
            const location = await getCurrentLocation();
            pendingLocationRef.current = location;
            setPendingPunchType(type);
            setCameraOpen(true);
        } catch (err) {
            swal("Error", err.message || "Failed to get location", "error");
        } finally {
            setActionLoading(null);
        }
    };

    // Step 2: called after photo is confirmed in camera modal
    const handlePhotoConfirmed = async (blob, previewUrl) => {
        setCameraOpen(false);
        const type = pendingPunchType;
        const location = pendingLocationRef.current;
        pendingLocationRef.current = null;
        setPendingPunchType(null);

        if (!blob || !location) {
            swal("Error", "Missing photo or location. Please try again.", "error");
            return;
        }

        try {
            setActionLoading(type);
            const url = type === "in" ? "/staff-attendance/self/punch-in" : "/staff-attendance/self/punch-out";

            const formData = new FormData();
            formData.append("location", JSON.stringify(location));
            formData.append("photo", blob, `punch-${type}-${Date.now()}.jpg`);

            const res = await api.post(url, formData, {
                headers: { "Content-Type": "multipart/form-data" },
            });

            setToday(res.data.record || null);
            swal("Success", res.data.message || "Attendance updated", "success");
            loadAttendance(appliedFilters);
        } catch (error) {
            swal("Error", error.response?.data?.message || error.message || "Failed to update attendance", "error");
        } finally {
            setActionLoading(null);
            if (previewUrl) URL.revokeObjectURL(previewUrl);
        }
    };

    const handleCameraCancel = () => {
        setCameraOpen(false);
        setPendingPunchType(null);
        pendingLocationRef.current = null;
    };

    return (
        <Fragment>
            <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
                <PageTitle activeMenu="My Attendance" motherMenu="HR" />
                <Button
                    variant="success"
                    className="d-flex align-items-center gap-1 shadow-sm"
                    onClick={() => navigate("/live-tracking")}
                >
                    My Live GPS & Route
                </Button>
            </div>

            <CameraModal
                show={cameraOpen}
                punchType={pendingPunchType}
                onCapture={handlePhotoConfirmed}
                onCancel={handleCameraCancel}
            />

            {accessMessage ? (
                <Card>
                    <Card.Body className="text-center py-5">
                        <h5 className="mb-2">Self Attendance Not Available</h5>
                        <p className="text-muted mb-0">{accessMessage}</p>
                    </Card.Body>
                </Card>
            ) : loading ? (
                <div className="text-center py-5"><Spinner /></div>
            ) : (
                <>
                    <Row className="g-3 mb-3">
                        {/* Staff Info */}
                        <Col lg={4} md={6}>
                            <Card className="h-100">
                                <Card.Body>
                                    <div className="text-muted small">Staff</div>
                                    <h5 className="mb-1">{employee?.userId?.name || "Staff"}</h5>
                                    <div className="text-muted small mb-2">{employee?.userId?.email || employee?.userId?.mobile || "-"}</div>
                                    <div className="d-flex align-items-center gap-1 flex-wrap">
                                        <span className="small text-muted" style={{ fontSize: 11 }}>Punch Location:</span>
                                        {Array.isArray(employee?.assignedOffices) && employee.assignedOffices.length > 0 ? (
                                            employee.assignedOffices.map((off, oIdx) => (
                                                <Badge key={oIdx} bg="info" className="fw-normal" style={{ fontSize: 11 }}>
                                                    <i className="fas fa-building me-1"></i>{off}
                                                </Badge>
                                            ))
                                        ) : (
                                            <Badge bg="success" className="bg-opacity-10 text-success border border-success fw-normal" style={{ fontSize: 11 }}>
                                                Any Location (Unrestricted)
                                            </Badge>
                                        )}
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>

                        {/* Today Status */}
                        <Col lg={4} md={6}>
                            <Card className="h-100">
                                <Card.Body>
                                    <div className="text-muted small">Today Status</div>
                                    <h5 className="mb-1">
                                        {todayState === "IN" && "Punched In"}
                                        {todayState === "OUT" && "Completed"}
                                        {todayState === "NOT_PUNCHED" && "Not Punched"}
                                    </h5>
                                    <div className="text-muted small">
                                        In: {formatDateTime(today?.punchInAt)} | Out: {formatDateTime(today?.punchOutAt)}
                                    </div>
                                    {punchDisabledMessage && <div className="text-warning small mt-2">{punchDisabledMessage}</div>}
                                    {/* Show today's captured photos */}
                                    {(today?.photoIn || today?.photoOut) && (
                                        <div className="d-flex gap-2 mt-2">
                                            {today.photoIn && (
                                                <a href={getFileUrl(today.photoIn)} target="_blank" rel="noreferrer">
                                                    <img src={getFileUrl(today.photoIn)} alt="In" className="punch-thumb" title="Punch In Photo" />
                                                </a>
                                            )}
                                            {today.photoOut && (
                                                <a href={getFileUrl(today.photoOut)} target="_blank" rel="noreferrer">
                                                    <img src={getFileUrl(today.photoOut)} alt="Out" className="punch-thumb" title="Punch Out Photo" />
                                                </a>
                                            )}
                                        </div>
                                    )}
                                </Card.Body>
                            </Card>
                        </Col>

                        {/* Punch Buttons */}
                        <Col lg={4} md={12}>
                            <Card className="h-100">
                                <Card.Body className="d-flex flex-column justify-content-center gap-2">
                                    <Button
                                        variant="success"
                                        size="lg"
                                        disabled={Boolean(actionLoading) || !canPunchToday || todayState !== "NOT_PUNCHED"}
                                        onClick={() => startPunch("in")}
                                    >
                                        {actionLoading === "in" ? <><Spinner size="sm" className="me-2" />Getting location...</> : "Punch In"}
                                    </Button>
                                    <Button
                                        variant="danger"
                                        size="lg"
                                        disabled={Boolean(actionLoading) || todayState !== "IN"}
                                        onClick={() => startPunch("out")}
                                    >
                                        {actionLoading === "out" ? <><Spinner size="sm" className="me-2" />Getting location...</> : "Punch Out"}
                                    </Button>
                                    <div className="text-muted small text-center">Location + photo required</div>
                                    {punchDisabledMessage && !canPunchToday && todayState === "NOT_PUNCHED" && (
                                        <div className="text-danger small text-center fw-medium mt-1">
                                            {punchDisabledMessage}
                                        </div>
                                    )}
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>

                    {/* History */}
                    <Card className="punch-history-card">
                        <Card.Header className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                            <div>
                                <Card.Title className="mb-1">My Punch History</Card.Title>
                                <div className="text-muted small">{history.length} records | {activeFilterCount} active filters</div>
                            </div>
                        </Card.Header>
                        <Card.Body>
                            <div className="attendance-filter-panel mb-3">
                                <Row className="g-2 align-items-end">
                                    <Col xl={2} lg={3} md={4} sm={6}>
                                        <Form.Label>From Date</Form.Label>
                                        <Form.Control type="date" value={filters.fromDate} onChange={(e) => updateFilter("fromDate", e.target.value)} />
                                    </Col>
                                    <Col xl={2} lg={3} md={4} sm={6}>
                                        <Form.Label>To Date</Form.Label>
                                        <Form.Control type="date" value={filters.toDate} onChange={(e) => updateFilter("toDate", e.target.value)} />
                                    </Col>
                                    <Col xl={2} lg={3} md={4} sm={6}>
                                        <Form.Label>Status</Form.Label>
                                        <Form.Select value={filters.status} onChange={(e) => updateFilter("status", e.target.value)}>
                                            {statusOptions.map((item) => <option key={item.value || "all"} value={item.value}>{item.label}</option>)}
                                        </Form.Select>
                                    </Col>
                                    <Col xl={2} lg={3} md={4} sm={6} className="attendance-filter-actions">
                                        <div className="d-flex gap-2">
                                            <Button variant="primary" className="flex-fill" onClick={applyFilters}>Search</Button>
                                            <Button variant="outline-secondary" onClick={clearFilters}>Clear</Button>
                                        </div>
                                    </Col>
                                </Row>
                            </div>

                            <Table bordered hover responsive className="punch-history-table align-middle">
                                <thead>
                                    <tr>
                                        <th className="text-center" style={{ width: 44 }}>#</th>
                                        <th style={{ minWidth: 100 }}>Date</th>
                                        <th style={{ minWidth: 130 }}>Punch In</th>
                                        <th style={{ minWidth: 130 }}>Punch Out</th>
                                        <th className="text-center" style={{ width: 90 }}>Hours</th>
                                        <th className="text-center" style={{ width: 72 }}>In Photo</th>
                                        <th className="text-center" style={{ width: 72 }}>Out Photo</th>
                                        <th style={{ minWidth: 160 }}>Location</th>
                                        <th className="text-center" style={{ width: 100 }}>Status</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {history.length === 0 ? (
                                        <tr><td colSpan="9" className="text-center text-muted py-4">No punch records found</td></tr>
                                    ) : history.map((item, idx) => (
                                        <tr key={item._id}>
                                            <td className="text-center text-muted small">{idx + 1}</td>
                                            <td className="text-nowrap small fw-semibold">
                                                {item.dateKey
                                                    ? new Date(`${item.dateKey}T00:00:00`).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })
                                                    : item.punchInAt
                                                        ? new Date(item.punchInAt).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })
                                                        : "-"}
                                            </td>
                                            <td className="text-nowrap small">
                                                {item.punchInAt
                                                    ? new Date(item.punchInAt).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })
                                                    : <span className="text-muted">-</span>}
                                            </td>
                                            <td className="text-nowrap small">
                                                {item.punchOutAt
                                                    ? new Date(item.punchOutAt).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })
                                                    : <span className="text-muted">-</span>}
                                            </td>
                                            <td className="text-center small fw-semibold">{formatDuration(item.totalMinutes)}</td>
                                            <td className="text-center">
                                                {item.photoIn
                                                    ? <a href={getFileUrl(item.photoIn)} target="_blank" rel="noreferrer" title="Punch In Photo">
                                                        <img src={getFileUrl(item.photoIn)} alt="In" className="punch-thumb" />
                                                    </a>
                                                    : <span className="text-muted small">—</span>}
                                            </td>
                                            <td className="text-center">
                                                {item.photoOut
                                                    ? <a href={getFileUrl(item.photoOut)} target="_blank" rel="noreferrer" title="Punch Out Photo">
                                                        <img src={getFileUrl(item.photoOut)} alt="Out" className="punch-thumb" />
                                                    </a>
                                                    : <span className="text-muted small">—</span>}
                                            </td>
                                            <td className="small">
                                                {item.locationIn?.latitude ? (
                                                    <div>
                                                        <span className="text-muted">In: </span>
                                                        <a
                                                            href={`https://www.google.com/maps?q=${item.locationIn.latitude},${item.locationIn.longitude}`}
                                                            target="_blank" rel="noreferrer"
                                                            className="text-decoration-none"
                                                        >
                                                            {Number(item.locationIn.latitude).toFixed(4)}, {Number(item.locationIn.longitude).toFixed(4)}
                                                        </a>
                                                    </div>
                                                ) : <div className="text-muted">In: —</div>}
                                                {item.locationOut?.latitude ? (
                                                    <div>
                                                        <span className="text-muted">Out: </span>
                                                        <a
                                                            href={`https://www.google.com/maps?q=${item.locationOut.latitude},${item.locationOut.longitude}`}
                                                            target="_blank" rel="noreferrer"
                                                            className="text-decoration-none"
                                                        >
                                                            {Number(item.locationOut.latitude).toFixed(4)}, {Number(item.locationOut.longitude).toFixed(4)}
                                                        </a>
                                                    </div>
                                                ) : <div className="text-muted">Out: —</div>}
                                                <div className="mt-1">
                                                    <Button
                                                        size="sm"
                                                        variant="outline-success"
                                                        style={{ fontSize: 10, padding: "2px 6px" }}
                                                        onClick={() => navigate(`/live-tracking?dateKey=${item.dateKey}`)}
                                                    >
                                                        Route Trail
                                                    </Button>
                                                </div>
                                            </td>
                                            <td className="text-center">
                                                <Badge bg={statusVariant[item.status] || "secondary"} className="text-uppercase" style={{ fontSize: 11, letterSpacing: "0.4px" }}>
                                                    {item.status?.replace(/_/g, " ") || "-"}
                                                </Badge>
                                                {item.requiresReview && (
                                                    <div><Badge bg="warning" text="dark" className="mt-1" style={{ fontSize: 10 }}>Review</Badge></div>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </Card.Body>
                    </Card>
                </>
            )}

            <style>{`
 /* Punch thumbs */
 .punch-thumb {
 width: 44px;
 height: 44px;
 object-fit: cover;
 border-radius: 6px;
 border: 1px solid #dee2e6;
 cursor: pointer;
 transition: transform 0.15s, box-shadow 0.15s;
 display: block;
 margin: 0 auto;
 }
 .punch-thumb:hover {
 transform: scale(1.12);
 box-shadow: 0 2px 8px rgba(0,0,0,0.18);
 }

 /* History table */
 .punch-history-table thead th {
 font-size: 11px;
 font-weight: 700;
 text-transform: uppercase;
 letter-spacing: 0.5px;
 color: #667085;
 white-space: nowrap;
 background: #f8fafc;
 border-bottom: 2px solid #e2e8f0;
 padding: 10px 12px;
 }
 .punch-history-table tbody td {
 padding: 10px 12px;
 vertical-align: middle;
 }
 .punch-history-table tbody tr:hover {
 background: #f0f7ff;
 }
 [data-theme-version="dark"] .punch-history-table thead th {
 background: #1a1d23;
 color: #9ca3af;
 border-bottom-color: #2b2f36;
 }
 [data-theme-version="dark"] .punch-history-table tbody tr:hover {
 background: #1e2330;
 }

 /* Filter panel */
 .attendance-filter-panel {
 border: 1px solid #e8edf5;
 border-radius: 12px;
 background: #ffffff;
 padding: 14px 18px;
 }
 .attendance-filter-panel .form-label {
 color: #7d86a2;
 font-size: 12px;
 font-weight: 600;
 margin-bottom: 8px;
 }
 .attendance-filter-panel .form-control,
 .attendance-filter-panel .form-select,
 .attendance-filter-panel .btn {
 min-height: 44px;
 border-radius: 8px;
 }
 .attendance-filter-panel .form-control,
 .attendance-filter-panel .form-select { font-weight: 600; font-size: 13px; }
 .attendance-filter-actions { display: flex; align-items: flex-end; }
 .attendance-filter-actions > div { width: 100%; }

 [data-theme-version="dark"] .attendance-filter-panel {
 background: #1b1b1b;
 border-color: #2b2f36;
 }
 [data-theme-version="dark"] .attendance-filter-panel .form-label { color: #c9ced8; }
 [data-theme-version="dark"] .attendance-filter-panel .form-control,
 [data-theme-version="dark"] .attendance-filter-panel .form-select {
 background-color: #111314;
 border-color: #2b2f36;
 color: #ffffff;
 }
 [data-theme-version="dark"] .attendance-filter-panel .form-control:focus,
 [data-theme-version="dark"] .attendance-filter-panel .form-select:focus {
 border-color: #0d99ff;
 box-shadow: 0 0 0 0.2rem rgba(13,153,255,0.18);
 }
 [data-theme-version="dark"] .attendance-filter-panel input[type="date"]::-webkit-calendar-picker-indicator {
 filter: invert(1); opacity: 0.75;
 }
 [data-theme-version="dark"] .attendance-filter-panel .btn-outline-secondary {
 border-color: #ff5c5c; color: #ffffff;
 }
 [data-theme-version="dark"] .attendance-filter-panel .btn-outline-secondary:hover {
 background: #ff5c5c; border-color: #ff5c5c;
 }
 @media (max-width: 575.98px) {
 .attendance-filter-panel .form-control,
 .attendance-filter-panel .form-select,
 .attendance-filter-panel .btn { min-height: 42px; }
 }
 `}</style>
        </Fragment>
    );
};

export default StaffSelfAttendance;
